import { useApolloClient, useQuery } from '@apollo/client';
import type {
  CreatedAccountsType,
  WorkflowDataType,
} from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useEffect, useMemo, useState } from 'react';
import {
  FETCH_ARCHIVE_ACCOUNTS,
  FETCH_ARCHIVE_GOAL,
  FETCH_ARCHIVE_MANUAL_PROCESS,
  FETCH_ARCHIVE_SCHEDULED_TRANSFER,
  FETCH_ARCHIVE_SUB_ACCOUNT,
  FETCH_ARCHIVE_TRANSFERS,
} from '../archiveObjects.queries';

const fetchAllGoals = async (
  client: ReturnType<typeof useApolloClient>,
  goalIds: string[],
) => {
  const results = await Promise.all(
    goalIds.map((goalId) =>
      client.query({ query: FETCH_ARCHIVE_GOAL, variables: { goalId } }),
    ),
  );
  return results.map((r) => r.data?.fetchGoal?.goal).filter((g) => g != null);
};

const fetchAllSubAccounts = async (
  client: ReturnType<typeof useApolloClient>,
  subAccountIds: string[],
) => {
  const results = await Promise.all(
    subAccountIds.map((subAccountId) =>
      client.query({
        query: FETCH_ARCHIVE_SUB_ACCOUNT,
        variables: { subAccountId },
      }),
    ),
  );
  return results
    .map((r) => r.data?.fetchSubAccount?.subAccount)
    .filter((sa) => sa != null);
};

const fetchAllScheduledTransfers = async (
  client: ReturnType<typeof useApolloClient>,
  scheduledTransferIds: string[],
) => {
  const results = await Promise.all(
    scheduledTransferIds.map((scheduledTransferId) =>
      client.query({
        query: FETCH_ARCHIVE_SCHEDULED_TRANSFER,
        variables: { scheduledTransferId },
      }),
    ),
  );
  return results
    .map((r) => r.data?.fetchScheduledTransfer?.scheduledTransfer)
    .filter((st) => st != null);
};

const fetchAllExternalTransfers = async (
  client: ReturnType<typeof useApolloClient>,
  externalTransferIds: string[],
) => {
  const results = await Promise.all(
    externalTransferIds.map((id) =>
      client.query({
        query: FETCH_ARCHIVE_MANUAL_PROCESS,
        variables: { id },
      }),
    ),
  );
  return results
    .map((r) => r.data?.fetchManualProcess?.manualProcess)
    .filter((mp) => mp != null);
};

export const useArchiveObjectsData = (workflowData: WorkflowDataType) => {
  const client = useApolloClient();

  const accountIds = useMemo(
    () =>
      (workflowData.createdAccounts ?? []).map(
        (a: CreatedAccountsType) => a.id ?? '',
      ),
    [workflowData.createdAccounts],
  );
  const goalIds = useMemo(
    () => workflowData.createdGoalIds ?? [],
    [workflowData.createdGoalIds],
  );
  const subAccountIds = useMemo(
    () => workflowData.createdSubAccountIds ?? [],
    [workflowData.createdSubAccountIds],
  );
  const transferIds = useMemo(
    () =>
      (workflowData.createdTransfers ?? []).map((t: { id: string }) => t.id),
    [workflowData.createdTransfers],
  );
  const scheduledTransferIds = useMemo(
    () =>
      (workflowData.createdScheduledTransfers ?? []).map(
        (t: { id: string }) => t.id,
      ),
    [workflowData.createdScheduledTransfers],
  );
  const externalTransferIds = useMemo(
    () =>
      (workflowData.createdExternalTransfers ?? []).map(
        (t: { id: string }) => t.id,
      ),
    [workflowData.createdExternalTransfers],
  );

  const { data: accountsData, loading: accountsLoading } = useQuery(
    FETCH_ARCHIVE_ACCOUNTS,
    {
      variables: { input: { filter: { accountIds } } },
      skip: accountIds.length === 0,
    },
  );

  const { data: transfersData, loading: transfersLoading } = useQuery(
    FETCH_ARCHIVE_TRANSFERS,
    {
      variables: { input: { filter: { transferIds } } },
      skip: transferIds.length === 0,
    },
  );

  const [batchData, setBatchData] = useState({
    goals: [] as Awaited<ReturnType<typeof fetchAllGoals>>,
    subAccounts: [] as Awaited<ReturnType<typeof fetchAllSubAccounts>>,
    scheduledTransfers: [] as Awaited<
      ReturnType<typeof fetchAllScheduledTransfers>
    >,
    externalTransfers: [] as Awaited<
      ReturnType<typeof fetchAllExternalTransfers>
    >,
    loading: false,
  });

  useEffect(() => {
    const hasObjectsToFetch =
      goalIds.length > 0 ||
      subAccountIds.length > 0 ||
      scheduledTransferIds.length > 0 ||
      externalTransferIds.length > 0;

    if (!hasObjectsToFetch) return;

    let isStale = false;
    setBatchData((prev) => ({ ...prev, loading: true }));

    const fetchAll = async () => {
      const [goals, subAccounts, scheduledTransfers, externalTransfers] =
        await Promise.all([
          goalIds.length > 0 ? fetchAllGoals(client, goalIds) : [],
          subAccountIds.length > 0
            ? fetchAllSubAccounts(client, subAccountIds)
            : [],
          scheduledTransferIds.length > 0
            ? fetchAllScheduledTransfers(client, scheduledTransferIds)
            : [],
          externalTransferIds.length > 0
            ? fetchAllExternalTransfers(client, externalTransferIds)
            : [],
        ]);

      if (!isStale) {
        setBatchData({
          goals,
          subAccounts,
          scheduledTransfers,
          externalTransfers,
          loading: false,
        });
      }
    };

    fetchAll();
    return () => {
      isStale = true;
    };
  }, [
    client,
    goalIds,
    subAccountIds,
    scheduledTransferIds,
    externalTransferIds,
  ]);

  return {
    accounts: accountsData?.fetchAccounts?.accounts ?? [],
    goals: batchData.goals,
    subAccounts: batchData.subAccounts,
    transfers: transfersData?.fetchTransfers?.transfers ?? [],
    scheduledTransfers: batchData.scheduledTransfers,
    externalTransfers: batchData.externalTransfers,
    loading: accountsLoading || transfersLoading || batchData.loading,
  };
};
